import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { govService } from '../../services/api';

// Basic inline styles keeping consistency with Affiliates & Partners
const btnPrimary: React.CSSProperties = {
    background: '#16a34a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.2s'
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
    color: '#0f172a'
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6
};

// ============================================
// LOGIN FORM
// ============================================
function LocalGovLogin({ onLogin, councilName }: { onLogin: () => void, councilName: string }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        // Simulate login check
        setTimeout(() => {
            onLogin();
            setLoading(false);
        }, 800);
    };

    return (
        <div style={{ maxWidth: 400, margin: '0 auto', background: '#fff', padding: 32, borderRadius: 16, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#1f2937', marginBottom: 8, textAlign: 'center' }}>Officer Login</h2>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14, marginBottom: 24 }}>Official Portal for {councilName}</p>

            <button
                type="button"
                style={{
                    ...btnPrimary,
                    background: '#fff',
                    color: '#1f2937',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    marginBottom: 24
                }}
                onClick={() => alert(`Redirecting to ${councilName} Azure AD / SAML SSO...`)}
            >
                <i className="fab fa-microsoft text-blue-600"></i> Sign in with Council Identity
            </button>

            <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 24px', position: 'relative' }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }}></div>
                <span style={{ padding: '0 12px', fontSize: 12, color: '#94a3b8', background: '#fff' }}>OR CORPORATE PASSWORD</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }}></div>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Official Email (.gov.uk)</label>
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="officer@council.gov.uk" />
                </div>
                <div style={{ marginBottom: 24 }}>
                    <label style={labelStyle}>Password</label>
                    <input type="password" required value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} placeholder="••••••••" />
                </div>
                <button type="submit" style={btnPrimary} disabled={loading}>
                    {loading ? 'Authenticating...' : 'Secure Login'}
                </button>
            </form>
        </div>
    );
}

// ============================================
// REQUEST ACCESS FORM
// ============================================
function LocalGovRequest({ onSuccess, councilName }: { onSuccess: () => void, councilName: string }) {
    const [form, setForm] = useState({ name: '', email: '', role: 'Enforcement Officer' });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await govService.requestAccess({ ...form, borough: councilName });
            onSuccess();
        } catch (err) {
            alert('Failed to submit request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 450, margin: '0 auto', background: '#fff', padding: 40, borderRadius: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: '#111827', marginBottom: 8, textAlign: 'center' }}>Request Access</h2>
            <p style={{ textAlign: 'center', color: '#4b5563', fontSize: 15, marginBottom: 32 }}>Establish jurisdiction for inspection and enforcement.</p>

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Full Name</label>
                    <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} placeholder="John Doe" />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Council Email</label>
                    <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} placeholder="officer@council.gov.uk" />
                </div>
                <div style={{ marginBottom: 24 }}>
                    <label style={labelStyle}>Job Title / Dept</label>
                    <select
                        style={inputStyle}
                        value={form.role}
                        onChange={e => setForm({ ...form, role: e.target.value })}
                    >
                        <option>Environmental Health Officer</option>
                        <option>Housing Enforcement</option>
                        <option>Licensing Manager</option>
                        <option>Strategic Housing</option>
                    </select>
                </div>
                <button type="submit" style={btnPrimary} disabled={loading}>
                    {loading ? 'Submitting...' : 'Submit Request for Verification'}
                </button>
            </form>
            <p style={{ marginTop: 24, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                Access is restricted to verified UK Cabinet Office and Local Government personnel.
            </p>
        </div>
    );
}

// ============================================
// LANDING PAGE
// ============================================
export default function LocalGovLanding() {
    const { orgId } = useParams<{ orgId?: string }>();
    const [view, setView] = useState<'landing' | 'login' | 'request' | 'dashboard'>('landing');

    // Dynamic Title Logic
    const formatOrgName = (id?: string) => {
        if (!id || id === 'hub') return 'Regulatory Compliance Hub';

        // Handle "manchestercity" type strings
        return id
            .replace(/([A-Z])/g, ' $1') // Space before capitals
            .replace(/^./, (str) => str.toUpperCase()) // Uppercase first
            .replace(/council/gi, '') // Remove redundant "Council"
            .replace(/city/gi, ' City') // Ensure "City" has space
            .trim() + ' Council';
    };

    const councilName = formatOrgName(orgId);

    // Dynamic Image Logic: Looks for [orgId].png, falls back to manchester_city_council.png
    let bgImage = '/manchester_city_council.png';
    if (orgId && orgId.toLowerCase() !== 'hub' && orgId.toLowerCase() !== 'manchester_city_council' && orgId.toLowerCase() !== 'manchestercity') {
        bgImage = `/${orgId.toLowerCase()}.png`;
    }

    if (view === 'landing') {
        return (
            <div className="bg-white font-sans text-gray-900 min-h-screen flex flex-col relative overflow-hidden">
                {/* DYNAMIC BACKGROUND IMAGE - faint and full-screen */}
                <div
                    className="absolute inset-0 z-0 opacity-[0.12] pointer-events-none"
                    style={{
                        backgroundColor: '#f8fafc',
                        backgroundImage: `url("${bgImage}"), url("/manchester_city_council.png")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'grayscale(100%) brightness(0.95)'
                    }}
                />
                {/* Note: CSS allows multiple backgrounds; if the first is 404, it might skip, but better yet we use the primary variable logic */}

                {/* NAV */}
                <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-16">
                        <div className="flex items-center gap-2 text-decoration-none">
                            <div className="w-8 h-8 flex items-center justify-center rounded bg-emerald-600 shadow-sm">
                                <i className="fas fa-building text-white text-sm"></i>
                            </div>
                            <span className="font-bold text-lg text-gray-900">Landlord Pro <span className="text-gray-400 font-normal mx-2">|</span> <span className="text-gray-600 font-semibold">{councilName}</span></span>
                        </div>
                        <div className="flex items-center gap-4">
                            <button onClick={() => setView('login')} className="text-gray-600 hover:text-emerald-600 font-medium transition-colors">
                                Officer Login
                            </button>
                            <button onClick={() => setView('request')} className="bg-emerald-600 text-white px-5 py-2 rounded font-semibold hover:bg-emerald-700 transition shadow-sm">
                                Request Access
                            </button>
                        </div>
                    </div>
                </nav>

                {/* HERO */}
                <section className="relative z-10 py-24 md:py-36 flex-1 flex flex-col justify-center">
                    <div className="max-w-4xl mx-auto px-6 text-center">
                        <div className="inline-block bg-emerald-100/60 text-emerald-800 px-4 py-1.5 rounded-full text-sm font-bold mb-8 backdrop-blur-sm border border-emerald-200/50">
                            Official Regulatory Portal
                        </div>
                        <h1 className="text-5xl md:text-8xl font-black mb-8 text-gray-900 leading-[1] tracking-tight">
                            Neutralising <span className="text-emerald-600">Regulatory Friction</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto font-medium">
                            Direct oversight of property compliance and health standards across {councilName}. Instantly audit portfolios and issue notices with zero paperwork delays.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button onClick={() => setView('login')} className="bg-emerald-600 text-white px-10 py-5 rounded-2xl font-bold hover:bg-emerald-700 transition shadow-2xl shadow-emerald-600/20 text-lg flex items-center justify-center gap-3">
                                Access Regulatory Hub <i className="fas fa-arrow-right"></i>
                            </button>
                            <button onClick={() => setView('request')} className="bg-white/80 backdrop-blur-sm border border-gray-200 text-gray-700 px-10 py-5 rounded-2xl font-bold hover:bg-gray-50 transition text-lg">
                                Register Your Dept
                            </button>
                        </div>
                    </div>
                </section>

                {/* Features Bar */}
                <section className="relative z-10 py-12 border-t border-gray-100 bg-white/40 backdrop-blur-sm">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                            <div>
                                <h3 className="font-bold text-gray-900 mb-1">Instant Audits</h3>
                                <p className="text-sm text-gray-500">Live compliance timelines</p>
                            </div>
                            <div className="border-x border-gray-100">
                                <h3 className="font-bold text-gray-900 mb-1">Automated Notices</h3>
                                <p className="text-sm text-gray-500">Digital service of documents</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 mb-1">Gov.uk Integration</h3>
                                <p className="text-sm text-gray-500">Real-time national sync</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if (view === 'login') {
        return (
            <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
                <div style={{ padding: '20px 32px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={() => setView('landing')} className="text-emerald-600 font-semibold hover:text-emerald-700">← Back to Hub</button>
                </div>
                <div style={{ padding: '48px 24px' }}>
                    <LocalGovLogin onLogin={() => setView('dashboard')} councilName={councilName} />
                </div>
            </div>
        );
    }

    if (view === 'request') {
        return (
            <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
                <div style={{ padding: '20px 32px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={() => setView('landing')} className="text-emerald-600 font-semibold hover:text-emerald-700">← Back to Hub</button>
                </div>
                <div style={{ padding: '48px 24px' }}>
                    <LocalGovRequest onSuccess={() => setView('dashboard')} councilName={councilName} />
                </div>
            </div>
        );
    }

    // Check for Super Admin status
    const isSuperAdmin = localStorage.getItem('userRole') === 'SUPER_ADMIN' ||
        localStorage.getItem('isSuperAdmin') === 'true';

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
            <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fas fa-building" style={{ color: '#16a34a', fontSize: 24, marginRight: 8 }}></i>
                    <span style={{ color: '#1f2937', fontWeight: 700, fontSize: 18 }}>
                        Regulatory Hub <span style={{ color: '#6b7280', fontWeight: 400 }}>{isSuperAdmin ? 'Master Oversight' : 'Alpha'}</span>
                    </span>
                </div>
                <div className="flex gap-4 items-center">
                    {isSuperAdmin && (
                        <div className="bg-red-100 text-red-800 text-xs font-black px-3 py-1 rounded-full border border-red-200 uppercase tracking-tighter">
                            Super Admin Mode
                        </div>
                    )}
                    <button onClick={() => setView('landing')} className="text-sm px-4 py-2 border border-emerald-600 text-emerald-600 rounded">Logout</button>
                </div>
            </div>

            {isSuperAdmin ? (
                /* SUPER ADMIN VIEW - Unrestricted */
                <div style={{ padding: '40px 24px', maxWidth: 1200, margin: '0 auto' }}>
                    <div className="mb-8 flex justify-between items-end">
                        <div>
                            <h2 className="text-3xl font-black text-gray-900 tracking-tight">
                                Jurisdictional Oversight: <span className="text-emerald-600">{councilName}</span>
                            </h2>
                            <p className="text-gray-500 font-medium">Full unrestricted access granted via Super Admin privileges.</p>
                        </div>
                        <div className="flex gap-2">
                            <button className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg font-bold text-sm hover:bg-gray-50 shadow-sm transition">
                                <i className="fas fa-file-csv mr-2"></i>Export Borough Data
                            </button>
                            <button className="bg-gray-900 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-black shadow-sm transition">
                                <i className="fas fa-plus mr-2"></i>Issue Bulk Notice
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        {[
                            { label: 'Total Stock', value: '12,402', icon: 'fa-building', color: 'blue' },
                            { label: 'Critically Non-Compliant', value: '184', icon: 'fa-exclamation-triangle', color: 'red' },
                            { label: 'Revenue Generated', value: '£42,910', icon: 'fa-pound-sign', color: 'emerald' },
                            { label: 'Pending Inspections', value: '56', icon: 'fa-clipboard-list', color: 'amber' }
                        ].map((stat, i) => (
                            <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <div className={`w-10 h-10 rounded-xl bg-${stat.color}-100 flex items-center justify-center text-${stat.color}-600 mb-4`}>
                                    <i className={`fas ${stat.icon}`}></i>
                                </div>
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{stat.label}</div>
                                <div className="text-2xl font-black text-gray-900">{stat.value}</div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h3 className="font-bold text-gray-900">Live Compliance Feed</h3>
                            <button className="text-emerald-600 text-sm font-bold hover:underline">View All Records</button>
                        </div>
                        <table className="w-full">
                            <thead className="bg-gray-50 text-left text-xs text-gray-400 font-bold uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">Property</th>
                                    <th className="px-6 py-4">Landlord</th>
                                    <th className="px-6 py-4">Health Score</th>
                                    <th className="px-6 py-4">Last Audit</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {[
                                    { addr: '124 Deansgate, M3 2QW', owner: 'M. Properties Ltd', score: 92, date: '2h ago', status: 'Healthy' },
                                    { addr: '45 Spinningfields, M3 3BE', owner: 'City Living', score: 34, date: '5h ago', status: 'Critical' },
                                    { addr: '88 Portland St, M1 4GX', owner: 'Elite Estates', score: 78, date: '1d ago', status: 'Warning' },
                                    { addr: '12 Ancoats Ln, M4 6HT', owner: 'Urban Nest', score: 95, date: '2d ago', status: 'Healthy' }
                                ].map((row, i) => (
                                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4 font-bold text-gray-900 text-sm">{row.addr}</td>
                                        <td className="px-6 py-4 text-gray-600 text-sm">{row.owner}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-12 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                                    <div className={`h-full ${row.score < 50 ? 'bg-red-500' : row.score < 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${row.score}%` }}></div>
                                                </div>
                                                <span className="text-xs font-black">{row.score}%</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-400 text-xs font-medium">{row.date}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button className="text-emerald-600 hover:text-emerald-700 font-black text-xs uppercase tracking-widest">
                                                Inspect
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                /* REGULAR OFFICER VIEW - Access Under Review */
                <div style={{ padding: '48px 24px', maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
                    <div style={{ background: '#fff', padding: 48, borderRadius: 24, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                        <i className="fas fa-file-signature" style={{ fontSize: 64, color: '#94a3b8', marginBottom: 24 }}></i>
                        <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>Access Under Review</h2>
                        <p style={{ color: '#64748b', fontSize: 16, lineHeight: 1.6, maxWidth: 500, margin: '0 auto 32px' }}>
                            Your local government credentials for {councilName} are being verified by our compliance team. Full jurisdictional access will be granted shortly.
                        </p>
                        <div style={{ display: 'inline-block', background: '#fef3c7', color: '#92400e', padding: '8px 16px', borderRadius: 999, fontSize: 14, fontWeight: 700 }}>
                            Status: Pending Super-Admin Verification
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
